import { fragments } from 'config/dictionary';
import { path, pathOr, prop, propOr } from 'ramda';
import { getOperator } from './recipientBranding';

const BLUE_CLIENT_ID = process.env.REACT_APP_BLUE_CLIENT_ID;
const POST_OFFICE_CLIENT_ID = process.env.REACT_APP_POST_OFFICE_CLIENT_ID;
const BLUE_API_CLIENT_ID = process.env.REACT_APP_BLUE_API_CLIENT_ID;
const POST_OFFICE_API_CLIENT_ID =
  process.env.REACT_APP_POST_OFFICE_API_CLIENT_ID;
const POST_OFFICE_WHITE_LABEL_CLIENT_ID =
  process.env.REACT_APP_POST_OFFICE_WHITE_LABEL_CLIENT_ID;
const COOP_PRODUCT_ID = process.env.REACT_APP_COOP_PRODUCT_ID;
const PREPAID_HEATING_CARD_BRAND_IDS = (
  process.env.REACT_APP_PREPAID_HEATING_CARD_BRAND_ID || ''
)
  .split(',')
  .map((brandId) => brandId.trim())
  .filter(Boolean);

export const getSenderName = (huggg) => {
  const sender = propOr({}, 'sender', huggg);

  if (sender.send_as_name) {
    return sender.send_as_name;
  }

  const name = `${propOr('', 'first_name', sender)} ${propOr(
    '',
    'surname',
    sender,
  )}`;
  return name;
};

/**
 * The displayed label for a Huggg varies whether it is consolidated or not.
 * Return the correct generated string based on this.
 *
 * @param {Object} huggg
 * @returns {String}
 */
export const getExpandedLabel = (huggg) => {
  const purchase = prop('purchase', huggg);
  const brand = path(['purchase', 'brand'], huggg);
  const label = propOr(
    getOperator(huggg).productNoun ?? 'huggg',
    'label',
    purchase,
  );
  const name =
    brand && isConsolidated(huggg)
      ? fragments.consolidatedName
      : prop('name', brand);

  return name ? `${label} at ${name}` : label;
};

/**
 * Has the provided huggg already been redeemed
 *
 * @param {Object} huggg
 * @returns {Boolean}
 */
export const isRedeemed = (huggg) => Boolean(huggg.redeemed_id);

/**
 * Is the provided huggg a super huggg. A super huggg is a single huggg that
 * can be redeemed multiple times.
 *
 * @param {Object} huggg
 * @returns {Boolean}
 */
export const isSuperHuggg = (huggg) => huggg.isSuperhuggg;

/**
 * Is the provided huggg a consolidated huggg. This is buried deep in the data.
 *
 * @param {Object} huggg
 * @returns {Boolean}
 */
export const isConsolidated = ({ purchase: { brand } }) =>
  Boolean(brand.consolidated);

export const getBrandName = (huggg) =>
  pathOr(null, ['purchase', 'brand_name'], huggg);

export const getCoverImageUrl = (huggg) => prop('cover_image_url', huggg);

const transformReaction = (reaction) => {
  if (!reaction) {
    return undefined;
  }

  return {
    reaction: {
      loadingEmoji: false,
      loadingComment: false,
      loadingEmojiError: '',
      loadingCommentError: '',
      emoji: reaction?.emoji || '',
      name: reaction?.name || '',
      message: reaction?.message || '',
      id: reaction?.id || undefined,
    },
  };
};

const getAllSegments = (huggg) => {
  const items = pathOr(null, ['purchase', 'items'], huggg);
  return items || pathOr([], ['purchase', 'segments'], huggg);
};

const getCustomSegments = (huggg) => {
  const list = pathOr(null, ['meta', 'gwcSegments'], huggg);

  // @later we can use the images on the prop here, but I just want to get
  // this out now!
  return list && Array.isArray(list) && list.length
    ? list.map(({ id }) => id)
    : null;
};

/**
 * There is optional meta data that can be passed through for the GWC
 * which should filter the displayed segments.
 *
 * If this fails to match valid choices correctly, we'll fallback to
 * the original segment behaviour.
 */
export const getSegments = (huggg) => {
  const includeList = getCustomSegments(huggg);
  const allSegments = getAllSegments(huggg);

  if (!includeList) {
    return allSegments;
  }

  const filtered = allSegments.filter((segment) =>
    includeList.includes(segment.id),
  );

  return filtered.length ? filtered : allSegments;
};

export const transformHuggg = (huggg) => {
  return {
    huggg: {
      ...huggg,
      ...(huggg && huggg.purchase
        ? {
            purchase: {
              ...huggg.purchase,
              segments: getSegments(huggg),
            },
          }
        : {}),
    },
    brand: pathOr({}, ['purchase', 'brand'], huggg),
    locations: pathOr(null, ['purchase', 'brand', 'locations'], huggg),
    message: propOr({}, 'message', huggg),
    ...transformReaction(propOr(undefined, 'reaction', huggg)),
  };
};

export const redeemInstructions = (huggg) =>
  path(['purchase', 'redemption_instructions'], huggg);

export const getProductName = (huggg) =>
  pathOr(
    getOperator(huggg).productNoun ?? 'huggg',
    ['purchase', 'label'],
    huggg,
  );

export const getBasePathname = (huggg) => {
  if (huggg.isSuperhuggg) {
    return `/super/${huggg.shortcode}`;
  } else if (huggg.is_test) {
    return `/test/${huggg.shortcode}`;
  } else {
    return `/${huggg.shortcode}`;
  }
};

export const getVoucherAmount = ({
  pence,
  isGiftCard,
  variantLabel,
  alwaysShowDecimal = false,
}) => {
  if (variantLabel && isGiftCard) {
    // Remove the last word from the variant label
    // eg. €100 Germany -> €100, 200 BGN Bulgaria -> 200 BGN
    return variantLabel.replace(/\s+\S+$/, '');
  }

  if (pence) {
    const pounds = pence / 100;

    if (alwaysShowDecimal || pence % 100 !== 0) {
      return `£${pounds.toFixed(2)}`;
    }

    return `£${pounds.toFixed(0)}`;
  }
  return '';
};

const isVouchers = (purchaseClientId) =>
  [
    BLUE_CLIENT_ID,
    BLUE_API_CLIENT_ID,
    POST_OFFICE_CLIENT_ID,
    POST_OFFICE_API_CLIENT_ID,
    POST_OFFICE_WHITE_LABEL_CLIENT_ID,
  ]
    .filter(Boolean)
    .includes(purchaseClientId);

// Digital cheques share the bank-payment recipient treatment: 'a payment'
// wording and the dynamic per-recipient amount from sale_per_item.
const BANK_PAYMENT_FAMILY_TYPES = ['bank-payment', 'digital-cheque'];

const isBankPaymentHuggg = (huggg) =>
  BANK_PAYMENT_FAMILY_TYPES.includes(huggg.purchase?.behavioural_type);

// Only a digital cheque gives the recipient a choice between bank transfer and
// cash at the Post Office; a plain bank payment has no cash option.
export const isDigitalChequeHuggg = (huggg) =>
  huggg?.purchase?.behavioural_type === 'digital-cheque';

export const getHugggTypeText = (huggg) => {
  const segments = pathOr([], ['purchase', 'segments'], huggg);
  const isPin4 =
    segments.length === 1 &&
    pathOr(null, [0, 'voucherType'], segments) === 'pin4';

  const purchaseClientId = pathOr(null, ['purchase_client_id'], huggg);
  const isDeliverable = huggg.purchase?.behavioural_type === 'delivered';

  if (isBankPaymentHuggg(huggg)) {
    return 'a payment';
  }

  if (isVouchers(purchaseClientId)) {
    return isDeliverable ? 'an item' : `a voucher`;
  }

  return isPin4 ? 'a cash voucher' : 'a gift!';
};

export const getHugggTitle = (huggg) => {
  const purchaseClientId = pathOr(null, ['purchase_client_id'], huggg);
  const isDeliverable = huggg.purchase?.behavioural_type === 'delivered';
  const isGiftCard = huggg.purchase?.behavioural_type === 'gift-card';
  const isBankPayment = isBankPaymentHuggg(huggg);
  const variantLabel = huggg.purchase?.variant_label;

  const voucherAmount = getVoucherAmount({
    // Bank payments are dynamic-value: the recipient amount is the transaction's
    // sale_per_item (threaded onto the huggg), not the product's sale_price
    // (which is 0 for bank payments).
    pence: isBankPayment ? huggg.sale_per_item : huggg.purchase?.sale_price,
    isGiftCard,
    variantLabel,
  });

  if (
    (isVouchers(purchaseClientId) && !isDeliverable && voucherAmount) ||
    (!isVouchers(purchaseClientId) && isGiftCard && voucherAmount) ||
    (isBankPayment && voucherAmount)
  ) {
    return huggg?.purchase?.label + ' - ' + voucherAmount;
  }

  return huggg?.purchase?.label;
};

export const makeTitle = (huggg) => {
  const { productNoun } = getOperator(huggg);
  if (productNoun) {
    return huggg.from_name
      ? `You've been sent a ${productNoun} from ${huggg.from_name}`
      : `You've been sent a ${productNoun}`;
  }
  const type = getHugggTypeText(huggg);
  const bang = type.endsWith('!') ? '!' : '';
  const typeText = bang ? type.slice(0, -1) : type;

  return `You've been sent ${typeText} from ${
    huggg.from_name ?? 'Huggg'
  }${bang}`;
};

export const makeReactionTitle = (huggg) => `Say thanks to ${huggg.from_name}`;
export const makeLandingTitle = (huggg) => {
  const type = getHugggTypeText(huggg);

  return `${huggg.from_name} sent you ${type}`;
};

/**
 * Returns true if every segment is a gift-card
 */
export const isGiftCard = (huggg) => {
  const segments = getSegments(huggg);
  return (
    segments &&
    segments.length &&
    segments.some((segment) =>
      ['tillo', 'wegift'].includes(propOr(null, 'voucherType', segment)),
    )
  );
};

/**
 * This is required as stored vouchers usually just render a QR code and so have no information,
 * about where they can be used.
 * Ideally, this should end up as a flag in the CMS at some point in the near future, but it'll do for now.
 */
export const shouldUseRedeemedSegmentInformation = (huggg) => {
  return !!(huggg.redeemedSegment?.name && huggg.redeemedSegment?.images?.[0]);
  // return (
  //   huggg.vouchers?.find(({ type }) => ['qr', 'barcode'].includes(type)) ||
  //   huggg.redeemedSegment?.voucherType === 'stored'
  // );
};

export const getRedeemedSegmentImage = (huggg) => {
  return huggg.redeemedSegment?.images?.[0];
};

export const getRedeemedSegmentName = (huggg) => {
  return huggg.redeemedSegment?.name;
};

export const needsCustomImage = (huggg) =>
  Boolean(getCustomSegments(huggg)) && !isGiftCard(huggg);

export const getSegmentImages = (huggg) =>
  getSegments(huggg)
    ?.map((segment) => segment.images[0] ?? null)
    .filter(Boolean) ?? [];

export const getHugggImage = (huggg) => {
  return (
    huggg?.purchase?.images?.redemptionCover?.[0] ??
    huggg?.purchase?.images?.main?.[0] ??
    huggg?.purchase?.image_url ??
    huggg?.image_url
  );
};

export const isSegmentOutOfStock = (segment) => {
  if (segment?.configuration?.outOfStock?.enabled) {
    return true;
  }

  // A group is unavailable only once every item inside it is.
  return (
    Array.isArray(segment?.items) &&
    segment.items.length > 0 &&
    segment.items.every((item) => item?.configuration?.outOfStock?.enabled)
  );
};

export const areAllSegmentsOutOfStock = (huggg) => {
  const segments = getSegments(huggg);

  return (
    Array.isArray(segments) &&
    segments.length > 0 &&
    segments.every(isSegmentOutOfStock)
  );
};

export const isCoopVoucher = (huggg) => {
  return huggg?.purchase_id && huggg?.purchase_id === COOP_PRODUCT_ID;
};

export const isPrepaidHeatingCard = (huggg) => {
  return PREPAID_HEATING_CARD_BRAND_IDS.includes(
    huggg?.huggg?.purchase?.brand_id,
  );
};
